/**
 * PDFViewerPanelPlaceholder Component - Placeholder when no citation selected
 *
 * Extracted from PDFViewerPanel.tsx
 */

import React from 'react';
import { FileText, AlertTriangle } from 'lucide-react';
import type { Theme } from '../../themes/theme';
import { PDFViewerPanelHeader } from './PDFViewerPanelHeader';

interface PDFViewerPanelPlaceholderProps {
  hideHeader: boolean;
  diagnosis: any;
  showDiagnosis: boolean;
  setShowDiagnosis: (show: boolean) => void;
  onClose?: () => void;
  theme: Theme;
}

export const PDFViewerPanelPlaceholder: React.FC<PDFViewerPanelPlaceholderProps> = ({
  hideHeader,
  diagnosis,
  showDiagnosis,
  setShowDiagnosis,
  onClose,
  theme,
}) => {
  return (
    <div className={`h-full w-full flex flex-col ${theme.colors.bg.primary}`}>
      {!hideHeader && <PDFViewerPanelHeader onClose={onClose} theme={theme} />}
      <div className={`flex-1 flex items-center justify-center ${theme.colors.bg.secondary}`}>
        <div className="text-center px-6">
          <FileText size={48} className={`mb-4 ${theme.colors.text.secondary}`} />
          <p className={`text-lg ${theme.colors.text.primary} mb-2`}>Visor de PDFs</p>
          <p className={`text-sm ${theme.colors.text.secondary} mb-4`}>
            Haz clic en una cita del chat para ver el documento
          </p>
          <div className={`text-xs ${theme.colors.text.muted} space-y-1`}>
            <p>• Navega por páginas con ← →</p>
            <p>• Zoom con + -</p>
            <p>• Busca texto con Ctrl+F</p>
          </div>

          {/* Diagnosis info */}
          {diagnosis && (
            <div className="mt-6 space-y-2">
              <button
                onClick={() => setShowDiagnosis(!showDiagnosis)}
                className={`text-xs ${theme.colors.text.secondary} hover:${theme.colors.text.primary} underline`}
              >
                {showDiagnosis ? '▼' : '▶'} Diagnóstico del sistema
              </button>

              {showDiagnosis && (
                <div
                  className={`text-left text-xs ${theme.colors.bg.tertiary} p-4 rounded-lg border ${theme.colors.border.primary} max-w-md`}
                >
                  <div className="space-y-2">
                    <div>
                      <span className={`font-semibold ${theme.colors.text.primary}`}>Usuario:</span>
                      <span className={`ml-2 ${theme.colors.text.secondary}`}>
                        {diagnosis.user?.username || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className={`font-semibold ${theme.colors.text.primary}`}>
                        Documentos:
                      </span>
                      <span className={`ml-2 ${theme.colors.text.secondary}`}>
                        {diagnosis.documents?.total || 0} total (
                        {diagnosis.documents?.user_docs || 0} propios,{' '}
                        {diagnosis.documents?.shared_docs || 0} compartidos)
                      </span>
                    </div>
                    <div>
                      <span className={`font-semibold ${theme.colors.text.primary}`}>
                        Archivos físicos:
                      </span>
                      <span className={`ml-2 ${theme.colors.text.secondary}`}>
                        {diagnosis.file_system?.files_count || 0} PDFs
                      </span>
                    </div>
                    {diagnosis.errors && diagnosis.errors.length > 0 && (
                      <div className={`mt-3 pt-3 border-t ${theme.colors.border.focus}/30`}>
                        <span
                          className={`font-semibold ${theme.colors.action.danger} flex items-center gap-1`}
                        >
                          <AlertTriangle size={16} />
                          <span>Problemas detectados:</span>
                        </span>
                        <ul className="mt-1 space-y-1">
                          {diagnosis.errors.map((error: string, idx: number) => (
                            <li key={idx} className={`${theme.colors.action.danger} text-xs`}>
                              • {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
